import { Fragment } from 'react'

import { useTranslation } from 'react-i18next'

import { Alert, Box, Link, Text, VStack } from '@enechain/ecloud-designsystem'

import { issuerTypeLabel } from '~/components/bidding/common/bidIssuer'
import { AlertDismissButton } from '~/components/common/AlertDismissButton'
import type { BidCsvImportNotice } from '~/models/bidCsvImportReturn'
import { formatDateValue, parseIsoDate } from '~/models/dateTime'

const WEEKDAY_KEYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'] as const

type Props = {
  notice: BidCsvImportNotice
  /** いま画面で選んでいる受渡日。 この日の件数は太字の文字にする */
  selectedDate?: Date
  /** ワークフローで受渡日が固定されている間 true。 受渡日はすべて文字にする */
  isDateLocked?: boolean
  /** 受渡日のリンク押下。 その受渡日へ切り替える */
  onSelectDate?: (date: Date) => void
  /** 閉じる (×)。 無ければ × を出さない (取込画面の 0 件の案内) */
  onDismiss?: () => void
} & Omit<
  React.ComponentProps<typeof Alert>,
  'children' | 'description' | 'status' | 'title'
>

/**
 * 入札 CSV (スポット・時間前) を取り込んで入札画面へ移った時に、 画面の上部へ出す完了の案内
 * (スポット AC-SPOT-024 / 時間前 AC-BCI-038・AC-BCI-039。 × で閉じる)。
 * 実際に取り込んだ件数・受渡日ごとの件数 (各受渡日へのリンク)・発注主体区分ごとの件数・
 * (時間前のみ) 対象外にした件数を理由ごとに並べる。
 *
 * 置き方は UI 辞書 alert-placement.md (画面の最上部。 業務上の優先度は「知っておけばよい」
 * なので、 締切や状態の Alert より下)。 取込画面の 0 件の案内 (warning) にも使う。
 */
export const BidCsvImportDoneAlert = ({
  notice,
  selectedDate,
  isDateLocked = false,
  onSelectDate,
  onDismiss,
  ...alertProps
}: Props): React.ReactElement => {
  const { t, i18n } = useTranslation()
  // 受渡日の日付部分は日本語 `M/D`、 英語 `YYYY-MM-DD` (english-ui-text-guide.md)。
  // 提出管理の対象日 (`formatTargetDateLabel`) と同じ分け方。 曜日と括弧は辞書で組む
  const isEnglish = i18n.language.startsWith('en')
  const selectedValue =
    selectedDate === undefined ? undefined : formatDateValue(selectedDate)
  const { excluded } = notice

  const showByDate = notice.importedCount > 0 && notice.byDate.length > 0
  const showByIssuer = notice.importedCount > 0 && notice.byIssuer.length > 0
  const hasDescription =
    showByDate ||
    showByIssuer ||
    excluded.gcClosed > 0 ||
    excluded.issuerUnregistered > 0

  // 見出しは `title`、 内訳は `description` に渡し、 children には閉じる (×) だけを入れる
  // (UI 辞書 components/alert-placement.md §4「React (DS `Alert`) での組み方」)。 見出しや本文を
  // children に入れると DS が空の本文の Stack を別に描き、 本文が右にずれる。
  // 内訳の文字は Alert の本文の 14px (8080 `.ecl-alert-desc` = textMd)。
  return (
    <Alert
      flexShrink={0}
      {...alertProps}
      description={
        hasDescription ? (
          <VStack alignItems="stretch" gap={1}>
            {showByDate && (
              <Text>
                {notice.byDate.map((entry, index) => {
                  const target = parseIsoDate(entry.date)
                  // 日付はカレンダー日なのでローカルの getter で曜日を求める
                  const label =
                    target === undefined
                      ? entry.date
                      : t('page:biddingCommon.csvImportDone.dateLabel', {
                          date: isEnglish
                            ? entry.date
                            : `${target.getMonth() + 1}/${target.getDate()}`,
                          weekday: t(
                            `common:date.calendar.weekday.${WEEKDAY_KEYS[target.getDay()]}`,
                          ),
                        })
                  const isSelected = entry.date === selectedValue
                  return (
                    <Fragment key={entry.date}>
                      {index > 0 && ' / '}
                      {isSelected ||
                      isDateLocked ||
                      target === undefined ||
                      onSelectDate === undefined ? (
                        <Box
                          as="span"
                          fontWeight={isSelected ? 'semibold' : undefined}
                        >
                          {label}
                        </Box>
                      ) : (
                        // 受渡日ごとのリンクは spec の指定 (AC-SPOT-024「各受渡日の入札一覧へのリンク」)
                        <Link asChild>
                          <button
                            onClick={() => onSelectDate(target)}
                            type="button"
                          >
                            {label}
                          </button>
                        </Link>
                      )}
                      {t('page:biddingCommon.csvImportDone.countSuffix', {
                        count: entry.count,
                      })}
                    </Fragment>
                  )
                })}
              </Text>
            )}
            {showByIssuer && (
              <Text>
                {notice.byIssuer
                  .map((entry) =>
                    t('page:biddingCommon.csvImportDone.issuerItem', {
                      issuer: issuerTypeLabel(entry.issuerType),
                      count: entry.count,
                    }),
                  )
                  .join(' / ')}
              </Text>
            )}
            {excluded.gcClosed > 0 && (
              <Text>
                {t('page:biddingCommon.csvImportDone.excludedGcClosed', {
                  count: excluded.gcClosed,
                })}
              </Text>
            )}
            {excluded.issuerUnregistered > 0 && (
              <Text>
                {t(
                  'page:biddingCommon.csvImportDone.excludedIssuerUnregistered',
                  { count: excluded.issuerUnregistered },
                )}
              </Text>
            )}
          </VStack>
        ) : undefined
      }
      status={notice.importedCount === 0 ? 'warning' : 'success'}
      title={
        notice.importedCount > 0
          ? t('page:biddingCommon.csvImportDone.title', {
              count: notice.importedCount,
            })
          : undefined
      }
    >
      {onDismiss !== undefined && <AlertDismissButton onClick={onDismiss} />}
    </Alert>
  )
}
